import { ChangeDetectionStrategy, Component, inject, Injectable } from "@angular/core";
import {
    AbstractControl,
    FormBuilder,
    FormGroup,
    ValidationErrors,
    ValidatorFn
} from "@angular/forms";
import { Observable, Subject } from "rxjs";

import { LgTranslateService, useTranslationNamespace } from "@logex/framework/lg-localization";
import {
    getDialogFactoryBase,
    IDialogComponent,
    LgDialogFactory,
    LgDialogRef
} from "@logex/framework/ui-core";

import { LocalComment } from "@shared";

@Component({
    selector: "mod-local-comment-edit",
    templateUrl: "./mod-local-comment-dialog.component.html",
    providers: [useTranslationNamespace("APP._BestPractices")],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class LocalCommentEditDialogComponent
    implements IDialogComponent<LocalCommentEditDialogComponent>
{
    private _onSave = new Subject<string>();
    private _dialogRef = inject(LgDialogRef<LocalCommentEditDialogComponent>);
    private _formBuilder = inject(FormBuilder);

    _dialogClass = "lg-dialog lg-dialog--3col";
    _title = inject(LgTranslateService).translate(".LocalComment");
    _form: FormGroup;
    _id: string;
    _name: string;
    lastChangeDate: string | undefined;
    lastChangeUserName: string | undefined;

    public get _isSubmitDisabled(): boolean {
        return this._form.invalid || !this._form.dirty;
    }

    public readOnly: boolean;

    open(localComment: LocalComment): Observable<string> {
        this._id = localComment.id;
        this._name = localComment.name;
        this.lastChangeDate = localComment.lastChangeDate;
        this.lastChangeUserName = localComment.lastChangeUserName;
        this.readOnly = localComment.readOnly;
        this._form = this._formBuilder.group({
            comment: [
                { value: localComment.comment, disabled: localComment.readOnly },
                noWhitespaceOnlyValidator()
            ]
        });
        return this._onSave.asObservable();
    }

    _onSubmit(): void {
        if (this._form.valid) {
            this._onSave.next(this._form.get("comment")?.value);
            this._onClose();
        }
    }

    _onClose(): void {
        this._onSave.complete();
        this._dialogRef.close();
    }

    _onCancel(): void {
        this._onClose();
    }
}

@Injectable()
export class LocalCommentEditDialog extends getDialogFactoryBase(
    LocalCommentEditDialogComponent,
    "open"
) {
    constructor() {
        super(inject(LgDialogFactory));
    }
}

function noWhitespaceOnlyValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
        const result = /^\s+$/.test(control.value);
        return result ? { onlywhitespace: { value: control.value } } : null;
    };
}
